import RailsUtility from '../utilities/rails';
import { hFetch, publishErrorUnlessSuccess, type HError } from './hFetch';
import type { ContactEntity } from './entity_types/Contact';
import type { JobDetailEntity, JobLocationEntity, V2JobEntity } from './entity_types/Job';
import type { VersionEntity } from './entity_types/Version';

type ListJobsParams = {
  status: string;
  sort: string;
  order: string;
};

type ListJobsResults = {
  records: V2JobEntity[];
  total_active: number;
  total_inactive: number;
};

export const listJobs = async (queryParams: ListJobsParams) => {
  // Convert status to API format if present
  const apiParams = {
    ...queryParams,
    status: queryParams.status ? RailsUtility.convertStatusToApi(queryParams.status) : queryParams.status,
  };

  const data = await hFetch<ListJobsResults>({ url: `/api/v2_jobs?${new URLSearchParams(apiParams).toString()}` });
  publishErrorUnlessSuccess({ data: data as unknown as HError, alertTitle: 'Failed to Get Jobs' });

  // Convert status back to frontend format in the response
  if (data && data.records) {
    data.records = data.records.map((record: V2JobEntity) => ({
      ...record,
      status: RailsUtility.convertStatusFromApi(record.status),
    }));
  }

  return data;
};

export type GetJobResponse = {
  record: V2JobEntity;
  is_stale: boolean;
  contacts: ContactEntity[];
  versions: VersionEntity[];
  draft_job_details: null;
};

export const getJob = async ({ id }: { id: number }) => {
  const data = await hFetch({ url: `/api/v2_jobs/${id}` });
  publishErrorUnlessSuccess({ data: data as unknown as HError, alertTitle: 'Failed to Get Job' });
  return data as GetJobResponse;
};

type UpdateJobAttributes = {
  status: string;
  resume_required: boolean;
  title: string;
  job_details: JobDetailEntity;
};

export const updateJob = async (
  { id }: { id: number },
  { updatedJobAttributes }: { updatedJobAttributes: UpdateJobAttributes }
) => {
  const data = await hFetch({ url: `/api/v2_jobs/${id}`, method: 'PATCH', payload: updatedJobAttributes });
  publishErrorUnlessSuccess({ data: data as unknown as HError, alertTitle: 'Failed to Update Job' });
  return data;
};

export const createJob = async (newJobAttributes: V2JobEntity) => {
  const data = await hFetch({ url: '/api/v2_jobs', method: 'POST', payload: newJobAttributes });
  publishErrorUnlessSuccess({ data: data as unknown as HError, alertTitle: 'Failed to Create Job' });
  return data;
};

export const createJobLocation = async (
  { id }: { id: number },
  { newJobLocationAttributes }: { newJobLocationAttributes: JobLocationEntity }
) => {
  const data = await hFetch({
    url: `/api/v2_jobs/${id}/job_locations`,
    method: 'POST',
    payload: newJobLocationAttributes,
  });
  publishErrorUnlessSuccess({ data: data as unknown as HError, alertTitle: 'Failed to Create Job Location' });
  return data;
};

export const deactivateJobLocation = async ({ jobId, locationId }: { jobId: string; locationId: string }) => {
  const data = await hFetch({
    url: `/api/v2_jobs/${jobId}/job_locations/${locationId}`,
    method: 'PATCH',
    payload: { status: 'inactive' },
  });
  publishErrorUnlessSuccess({ data: data as unknown as HError, alertTitle: 'Failed to Delete Job Location' });
  return data;
};
